'use client'

import type { ChainId, Fetchable } from '@morpho-org/blue-sdk'
import type { InterfaceToken } from '@morpho-org/dapp-core'

import { cn } from '@morpho-org/ui-v6/utils'
import { TruncatedTooltip } from '@repo/ui'
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'

import { Avatar, type AvatarSize } from './Avatar'

export interface TokenInfoProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Token object with symbol and logo */
  token: Fetchable<InterfaceToken>
  /** Chain ID */
  chainId?: ChainId
  /** Size of the avatar */
  avatarSize?: AvatarSize
  /** Render prop for warnings */
  renderWarnings?: ReactNode
  /** Render prop for user position indicator */
  renderUserPosition?: ReactNode
}

/**
 * TokenInfo component - Tailwind replica of dapp-ui-v5 TokenInfo
 *
 * Displays token information with avatar and symbol.
 * Accepts render props for warnings and indicators since these components
 * may be app-specific.
 *
 * Render it inside a link or focusable row: a clipped symbol leaves the tab order, so the
 * parent must carry the full value.
 *
 * @example
 * ```tsx
 * <TokenInfo
 *   token={token}
 *   renderWarnings={<WarningIcon warnings={warnings} />}
 * />
 * ```
 */
const TokenInfo = forwardRef<HTMLDivElement, TokenInfoProps>(
  (
    {
      token,
      chainId,
      avatarSize = 'medium',
      renderWarnings,
      renderUserPosition,
      className,
      ...props
    },
    ref
  ) => {
    if (!token) return null

    const { logoURI, symbol } = token

    return (
      <div ref={ref} className={cn('gap-2xs flex min-w-0 items-center', className)} {...props}>
        {logoURI && <Avatar src={logoURI} size={avatarSize} />}
        <TruncatedTooltip text={symbol ?? ''}>
          {(symbolRef, isTruncated) => (
            // Rendered inside table-row links: the link text already carries the symbol.
            <span
              ref={symbolRef}
              tabIndex={isTruncated ? -1 : undefined}
              className="text-foreground min-w-0 truncate text-xs leading-normal"
            >
              {symbol}
            </span>
          )}
        </TruncatedTooltip>
        {renderWarnings}
        {renderUserPosition}
      </div>
    )
  }
)

TokenInfo.displayName = 'TokenInfo'

export { TokenInfo }
